// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (c) 2026 Password Manager contributors
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { signOut } from "@/lib/auth";
import { native } from "@/native";
import { useAuthState } from "@/providers/AuthProvider";
import { LegalService, type LegalAcceptanceStatus } from "@/services/legal";
import { MarkdownLegalDocument } from "./MarkdownLegalDocument";

function needsAnyAcceptance(status: LegalAcceptanceStatus | undefined): boolean {
  return Boolean(
    status?.privacy.needsAcceptance || status?.terms.needsAcceptance,
  );
}

export function LegalAcceptanceGate() {
  const { t } = useTranslation("legal");
  const queryClient = useQueryClient();
  const { status, user } = useAuthState();
  const isAuthenticated = status === "authenticated" && Boolean(user);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);

  const statusQuery = useQuery({
    queryKey: ["legal", "acceptance-status", user?._id],
    queryFn: LegalService.getAcceptanceStatus,
    enabled: isAuthenticated,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  useEffect(() => {
    if (!native.isNative || !isAuthenticated) return;

    let cancelled = false;
    let handle: { remove: () => Promise<void> } | null = null;

    void native.app
      .addListener("appStateChange", ({ isActive }) => {
        if (isActive) {
          void statusQuery.refetch();
        }
      })
      .then((resolved) => {
        if (cancelled) {
          void resolved.remove();
          return;
        }
        handle = resolved;
      });

    return () => {
      cancelled = true;
      void handle?.remove();
    };
  }, [isAuthenticated, statusQuery.refetch]);

  const pendingDocs = useMemo(() => {
    const data = statusQuery.data;
    return {
      privacy: data?.privacy.needsAcceptance ? data.privacy.document : null,
      terms: data?.terms.needsAcceptance ? data.terms.document : null,
    };
  }, [statusQuery.data]);

  useEffect(() => {
    if (pendingDocs.privacy) setPrivacyChecked(false);
    if (pendingDocs.terms) setTermsChecked(false);
  }, [
    pendingDocs.privacy?.version,
    pendingDocs.terms?.version,
  ]);

  const acceptMutation = useMutation({
    mutationFn: LegalService.accept,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["legal", "acceptance-status", user?._id],
      });
    },
  });

  const handleDecline = async () => {
    await signOut();
    window.location.assign("/login");
  };

  const requiresPrivacy = Boolean(pendingDocs.privacy);
  const requiresTerms = Boolean(pendingDocs.terms);
  const canAccept =
    (!requiresPrivacy || privacyChecked) && (!requiresTerms || termsChecked);

  const handleAccept = () => {
    const data = statusQuery.data;
    if (!data) return;
    acceptMutation.mutate({
      privacyVersion: data.privacy.currentVersion,
      termsVersion: data.terms.currentVersion,
    });
  };

  const open = isAuthenticated && needsAnyAcceptance(statusQuery.data);

  return (
    <Dialog open={open} modal>
      <DialogContent
        className="max-w-[calc(100%-2rem)] sm:max-w-3xl [&>button]:hidden"
        onEscapeKeyDown={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{t("gate.title")}</DialogTitle>
          <DialogDescription>{t("gate.description")}</DialogDescription>
        </DialogHeader>

        {statusQuery.isLoading ? (
          <div className="flex min-h-48 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-5">
            {pendingDocs.privacy ? (
              <section className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-base font-semibold">
                    {t("privacy.title")}
                  </h2>
                  <span className="text-xs text-muted-foreground">
                    {t("common.version")}: {pendingDocs.privacy.version}
                  </span>
                </div>
                <ScrollArea className="h-56 rounded-lg border bg-muted/20 p-4">
                  <MarkdownLegalDocument
                    className="prose-sm"
                    markdown={pendingDocs.privacy.body}
                  />
                </ScrollArea>
                <div className="flex min-h-11 items-center gap-3">
                  <Checkbox
                    id="legal-privacy-agree"
                    checked={privacyChecked}
                    onCheckedChange={(checked) => setPrivacyChecked(checked === true)}
                  />
                  <Label htmlFor="legal-privacy-agree" className="text-sm">
                    {t("gate.perDocLabel.privacy")}
                  </Label>
                </div>
              </section>
            ) : null}

            {pendingDocs.terms ? (
              <section className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-base font-semibold">
                    {t("terms.title")}
                  </h2>
                  <span className="text-xs text-muted-foreground">
                    {t("common.version")}: {pendingDocs.terms.version}
                  </span>
                </div>
                <ScrollArea className="h-56 rounded-lg border bg-muted/20 p-4">
                  <MarkdownLegalDocument
                    className="prose-sm"
                    markdown={pendingDocs.terms.body}
                  />
                </ScrollArea>
                <div className="flex min-h-11 items-center gap-3">
                  <Checkbox
                    id="legal-terms-agree"
                    checked={termsChecked}
                    onCheckedChange={(checked) => setTermsChecked(checked === true)}
                  />
                  <Label htmlFor="legal-terms-agree" className="text-sm">
                    {t("gate.perDocLabel.terms")}
                  </Label>
                </div>
              </section>
            ) : null}
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleDecline}
            disabled={acceptMutation.isPending}
          >
            {t("gate.ctaDecline")}
          </Button>
          <Button
            type="button"
            onClick={handleAccept}
            disabled={!canAccept || acceptMutation.isPending || statusQuery.isLoading}
          >
            {acceptMutation.isPending ? (
              <Loader2 className="me-2 h-4 w-4 animate-spin" />
            ) : null}
            {t("gate.ctaAccept")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
